import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Rating,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  Star,
  StarBorder,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { reviewAPI, notificationAPI } from '../services/api';
import useResponsive from '../hooks/useResponsive';
import BottomNav from '../components/BottomNavigation';

const LeaveReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { booking, existingReview } = location.state || {};
  
  const { isMobile, getResponsiveSpacing } = useResponsive();

  const [rating, setRating] = useState(existingReview?.rating || 5);
  const [title, setTitle] = useState(existingReview?.title || '');
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [customerName, setCustomerName] = useState('');

  const isEditMode = !!existingReview;

  // Redirect if no booking data
  useEffect(() => {
    if (!booking) {
      navigate('/bookings');
    } else {
      // Get customer name from localStorage
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          setCustomerName(user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || user.firstName || 'Customer');
        } catch (error) {
          console.error('Error parsing user data:', error);
          setCustomerName('Customer');
        }
      } else {
        setCustomerName('Customer');
      }

      // Debug logging for booking data
      console.log('🔍 [REVIEW DEBUG] Booking data received:', {
        booking: booking,
        bookingId: booking.bookingId || booking._id || booking.id,
        providerId: booking.providerId || booking.provider?._id,
        bookingNumber: booking.bookingNumber,
        isEditMode: isEditMode,
        existingReview: existingReview
      });
    }
  }, [booking, navigate, isEditMode, existingReview]);

  const handleSubmitReview = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate
      if (!rating || rating < 1) {
        setError('Please select a rating');
        return;
      }

      const reviewData = {
        targetId: booking.providerId || booking.provider?._id,
        targetType: 'provider',
        rating: rating,
        title: title.trim(),
        comment: comment.trim(),
        bookingId: booking.bookingId || booking._id || booking.id
      };

      // Debug logging
      console.log('🔍 [REVIEW DEBUG] Submitting review with data:', {
        bookingId: reviewData.bookingId,
        targetId: reviewData.targetId,
        bookingData: booking,
        reviewData: reviewData,
        customerToken: localStorage.getItem('customerToken') ? 'Present' : 'Missing',
        token: localStorage.getItem('token') ? 'Present' : 'Missing'
      });

      let response;
      if (isEditMode) {
        // Update existing review
        response = await reviewAPI.updateReview(existingReview._id, reviewData);
      } else {
        // Submit new review
        response = await reviewAPI.submitReview(reviewData);
      }

      if (response.data.success) {
        // Send notification to provider for new reviews only
        if (!isEditMode) {
          try {
            const providerId = booking.providerId || booking.provider?._id;
            const bookingNumber = booking.bookingNumber;
            
            if (providerId && bookingNumber && customerName) {
              console.log('🔔 [NOTIFICATION DEBUG] Sending notification to provider:', {
                providerId,
                templateName: 'review_received_provider',
                variables: {
                  rating: rating,
                  customerName: customerName,
                  bookingNumber: bookingNumber
                }
              });

              await notificationAPI.sendNotificationToProvider(
                providerId,
                'review_received_provider',
                {
                  rating: rating,
                  customerName: customerName,
                  bookingNumber: bookingNumber
                }
              );
              
              console.log('✅ [NOTIFICATION DEBUG] Notification sent successfully to provider');
            } else {
              console.warn('⚠️ [NOTIFICATION DEBUG] Missing required data for notification:', {
                providerId: !!providerId,
                bookingNumber: !!bookingNumber,
                customerName: !!customerName
              });
            }
          } catch (notificationError) {
            console.error('❌ [NOTIFICATION DEBUG] Error sending notification to provider:', notificationError);
            // Don't block the review submission if notification fails
          }
        }

        // Show success dialog first
        setSuccessMessage(isEditMode ? 'Review updated successfully!' : 'Review submitted successfully!');
        setShowSuccessDialog(true);
        
        // Then navigate back after a short delay
        setTimeout(() => {
          navigate('/bookings', { 
            state: { 
              reviewSuccess: true,
              message: isEditMode ? 'Review updated successfully!' : 'Review submitted successfully!',
              updatedBookingId: booking._id || booking.id
            } 
          });
        }, 2000);
      }
    } catch (error) {
      console.error('❌ [REVIEW DEBUG] Error submitting review:', {
        error: error.response?.data,
        message: error.message,
        status: error.response?.status
      });
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        setError('You have already reviewed this booking. Please use the Edit button to update your review.');
        // Redirect back after showing error
        setTimeout(() => {
          navigate('/bookings');
        }, 3000);
      } else {
        setError(error.response?.data?.message || 'Failed to submit review. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!booking) {
    return null;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f5f5f5',
      pb: isMobile ? 8 : 4
    }}>
      {/* Header - Dark Blue */}
      <Box sx={{ 
        backgroundColor: '#1e3a8a', 
        color: 'white', 
        p: 1.5,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
            <IconButton 
              onClick={() => navigate('/bookings')}
              sx={{ color: 'white' }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'white' }}>
              {isEditMode ? 'Edit Review' : 'Leave a Review'}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', ml: 6, fontSize: '12px' }}>
            Share your experience with the provider
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ pt: 3 }}>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Main Review Card */}
        <Card elevation={2}>
          <CardContent>
            {/* Provider Info */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              pb: 2,
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Avatar 
                src={booking.providerLogo || booking.provider?.logoUrl} 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  mr: 2,
                  border: '2px solid #f0f0f0'
                }}
              >
                {booking.providerName?.charAt(0) || booking.provider?.businessName?.charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {booking.providerName || booking.provider?.businessName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {booking.serviceName || booking.service?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {booking.bookingNumber && `Booking #${booking.bookingNumber}`}
                </Typography>
              </Box>
            </Box>

            {/* Rating Section */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography 
                component="legend" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 500,
                  fontSize: '1.1rem'
                }}
              >
                How was your experience?
              </Typography>
              <Rating
                value={rating}
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setRating(newValue);
                  }
                }}
                size="large"
                icon={<Star fontSize="inherit" />}
                emptyIcon={<StarBorder fontSize="inherit" />}
                sx={{
                  fontSize: isMobile ? '3rem' : '3.5rem',
                  '& .MuiRating-iconFilled': {
                    color: '#FFB400',
                  },
                  '& .MuiRating-iconHover': {
                    color: '#FFA000',
                  }
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {rating === 5 && '⭐ Excellent!'}
                {rating === 4 && '👍 Great!'}
                {rating === 3 && '😊 Good'}
                {rating === 2 && '😕 Fair'}
                {rating === 1 && '😞 Poor'}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Title Field */}
            <TextField
              label="Review Title (Optional)"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: 100 }}
              helperText={`${title.length}/100 characters`}
              sx={{ mb: 2 }}
              placeholder="Summarize your experience"
            />

            {/* Comment Field */}
            <TextField
              label="Your Review"
              fullWidth
              multiline
              rows={isMobile ? 4 : 6}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              helperText={`${comment.length}/1000 characters`}
              placeholder="Share details of your experience..."
              sx={{ mb: 3 }}
            />

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/bookings')}
                disabled={loading}
                sx={{ 
                  height: 48,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmitReview}
                disabled={loading || rating === 0}
                sx={{ 
                  height: 48,
                  textTransform: 'none',
                  fontSize: '1rem',
                  bgcolor: '#4F46E5',
                  '&:hover': {
                    bgcolor: '#4338CA'
                  }
                }}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Submitting...' : (isEditMode ? 'Update Review' : 'Submit Review')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Tips Card */}
        <Card sx={{ mt: 2, bgcolor: '#f9fafb' }} elevation={1}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              💡 Tips for writing a great review:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>Be specific about what you liked or didn't like</li>
              <li>Mention the service quality and staff professionalism</li>
              <li>Share how the experience met your expectations</li>
              <li>Be honest and constructive</li>
            </Typography>
          </CardContent>
        </Card>
        </Container>

        {/* Success Dialog */}
        <Dialog 
          open={showSuccessDialog} 
          onClose={() => setShowSuccessDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Success!
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {successMessage}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Redirecting to bookings...
            </Typography>
          </DialogContent>
        </Dialog>

        {/* Bottom Navigation */}
        <BottomNav />
      </Box>
    );
  };

  export default LeaveReview;

